import React, { useEffect, useState } from "react";
import SchedulePill from "../schedulePill/SchedulePill";
import ProfilePicture from "../profilePicture/ProfilePicture";
import styles from './ScheduleCard.module.scss';
import PropTypes from "prop-types";
import { calculateTimeDifferenceHHMM, compareTimes, getDayFromDate, getMonthYearFromDate, getTimeHHMM } from "../../utils/timeUtils";
import Icon from "../icon/Icon";
import Modal from "../modal/Modal";
import LoadingOverlay from "../loadingOverlay/LoadingOverlay";
import TimePicker from "../timePicker/TimePicker";
import Button from "../button/Button";
import { isToday } from "date-fns";
import { useToasts } from "../../contexts/ToastContext";

const ScheduleCard = ({
  date,
  data,
  clockIn: initialClockIn,
  clockOut: initialClockOut,
  triggerChange,
  color = 'green',
  isLoading,
  onSubmit
}) => {
  const [showModal, setShowModal] = useState(false);
  const [clockIn, setClockIn] = useState(null);
  const [clockInError, setClockInError] = useState('');
  const [clockOut, setClockOut] = useState(null);
  const [clockOutError, setClockOutError] = useState('');
  const [type, setType] = useState('');
  const [coordinates, setCoordinates] = useState({ latitude: null, longitude: null });
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const { showToast } = useToasts();

  useEffect(() => {
    setClockIn(initialClockIn || null);
    setClockOut(initialClockOut || null);
  }, [initialClockIn, initialClockOut]);

  useEffect(() => {
    setShowModal(false);
  }, [triggerChange]);

  const validateClockIn = () => {
    if(!clockIn) {
      return 'Clock In is required';
    }
    if(type === 'editClockIn' && clockOut && !compareTimes(clockIn, clockOut)) {
      return 'Clock in cannot be later than or equal to clock out';
    }
    return '';
  }

  const validateClockOut = () => {
    if(!clockOut) {
      return 'Clock Out is required';
    }
    if(clockIn && !compareTimes(clockIn, clockOut)) {
      return 'Clock out cannot be earlier than or equal to clock in';
    }
    return '';
  }

  const openClockInModal = () => {
    setShowModal(true);
    if (navigator.geolocation) {
      setIsLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoordinates({ latitude, longitude });
          setIsLocationLoading(false);
        },
        (err) => {
          setIsLocationLoading(false);
          switch (err.code) {
            case err.PERMISSION_DENIED:
              showToast('User denied the request for Geolocation.', 'error');
              break;
            case err.POSITION_UNAVAILABLE:
              showToast('Location information is unavailable.', 'error');
              break;
            case err.TIMEOUT:
              showToast('The request to get user location timed out.', 'error');
              break;
            default:
              showToast(`Geolocation error: ${err.message}`, 'error');
          }
        },
        { timeout: 10000 }
      );
    } else {
      showToast('Geolocation is not supported by this browser.', 'error');
    }
  }

  const handleSubmit = async () => {
    const clockInValidationResult = validateClockIn();
    const clockOutValidationResult = ['clockOut', 'editClockOut'].includes(type) && validateClockOut();

    setClockInError(clockInValidationResult);
    setClockOutError(clockOutValidationResult);

    if(clockInValidationResult || clockOutValidationResult) {
      return false;
    }

    onSubmit(clockIn, clockOut, coordinates, type);
  };

  return (
    <div className={`${styles.card} ${isToday(date) ? styles.today : ''}`}>
      <div className={styles.content}>
        <div className={styles.date}>
          <div className={`${styles.day} ${styles[color] ?? ''}`}>{getDayFromDate(date)}</div>
          <div className={styles.month}>{getMonthYearFromDate(date)}</div>
        </div>
        <table className={styles.dataTable}>
          <tbody>
            {data.map((item) => (
              <tr key={item.label}>
                <th>{item.label}</th>
                <td>
                  {item.colorId ? (
                    <SchedulePill text={item.text} colorId={item.colorId} variant={item.variant} />
                  ) : item.instructor ? (
                    <div className={styles.instructorCell}>
                      <ProfilePicture
                        firstName={item.instructor.firstName}
                        lastName={item.instructor.lastName}
                        profilePicture={item.instructor.profilePicture}
                        size="xs"
                      />
                      <span>{item.text}</span>
                    </div>
                  ) : item.text}
                </td>
              </tr>
            ))}
            <tr>
              <th>Clock In</th>
              <td className={styles.clockInOutColumn}>
                {initialClockIn ?
                  <>
                    {initialClockIn}
                    {isToday(date) && <button
                      type="button"
                      className={styles.edit}
                      onClick={() => {
                        setType('editClockIn')
                        openClockInModal()
                        setClockIn(initialClockIn || getTimeHHMM(new Date()));
                      }}
                    >
                      Edit 
                    </button>}
                  </> : (isToday(date) ? <button
                    type="button"
                    className={styles.clockIn}
                    onClick={() => {
                      setType('clockIn')
                      openClockInModal()
                      setClockIn(initialClockIn || getTimeHHMM(new Date()));
                    }}
                  >
                    <Icon name="clock" /> Clock In 
                  </button> : '-')}
              </td>
            </tr>
            <tr>
              <th>Clock Out</th>
              <td className={styles.clockInOutColumn}>
                {initialClockOut ?
                  <>
                    {initialClockOut}
                    {isToday(date) && <button
                      type="button"
                      className={styles.edit}
                      onClick={() => {
                        setType('editClockOut')
                        openClockInModal()
                        setClockOut(initialClockOut || getTimeHHMM(new Date()));
                      }}
                    >
                      Edit 
                    </button>}
                  </> : ((isToday(date) && initialClockIn) ? <button
                    type="button"
                    className={styles.clockOut}
                    onClick={() => {
                      setType('clockOut')
                      openClockInModal()
                      setClockOut(initialClockOut || getTimeHHMM(new Date()));
                    }}
                  >
                    <Icon name="pause-circle" /> Clock Out 
                  </button> : '-')}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {showModal && <Modal scrollable={false}>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {(isLoading || isLocationLoading) && <LoadingOverlay />}
          <h3>Confirm the {['clockIn', 'editClockIn'].includes(type) ? 'clock in' : 'clock out'} time</h3>
          <div className={styles.fields}>
            <TimePicker
              label="Clock In"
              value={clockIn || null}
              onChange={(value) => setClockIn(value)}
              error={clockInError}
              disabled={['clockOut', 'editClockOut'].includes(type)}
            />
            {(['clockOut', 'editClockOut'].includes(type) || clockOut) && <TimePicker
              label="Clock Out"
              value={clockOut || null}
              onChange={(value) => setClockOut(value)}
              error={clockOutError}
              disabled={['clockIn', 'editClockIn'].includes(type)}
            />}
          </div>
          {clockIn && clockOut && <div className={styles.timeClocked}>
            Time Clocked <span>{calculateTimeDifferenceHHMM(clockIn, clockOut)}</span>
          </div>}
          <div className={styles.actions}>
            <Button
              label="Cancel"
              variant="white"
              size="medium"
              onClick={() => {
                setClockIn(initialClockIn);
                setClockInError('');
                setClockOut(initialClockOut);
                setClockOutError('');
                setShowModal(false)
              }}
            />
            <Button
              label={['clockIn', 'editClockIn'].includes(type) ? 'Clock In' : 'Clock Out'}
              variant="primary"
              size="medium"
              type="submit"
            />
          </div>
        </form>
      </Modal>}
    </div>
  );
};

ScheduleCard.propTypes = {
  date: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      text: PropTypes.any.isRequired,
    })
  ).isRequired,
  clockIn: PropTypes.string,
  clockOut: PropTypes.string,
  triggerChange: PropTypes.bool,
  color: PropTypes.oneOf(['green', 'orange']),
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
}

export default ScheduleCard;